import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
} from "@mui/material";
import {
  ComponentProps,
  memo,
  ReactNode,
  startTransition,
  TextareaHTMLAttributes,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import AnimateHeight from "react-animate-height";
import { useLocation, useNavigate } from "react-router-dom";
import QuizApi from "../../api/quizApi";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import Input from "../../components/Input/Input";
import Loader from "../../components/Loader/Loader";
import MuiSwitch from "../../components/MuiSwitch/MuiSwitch";
import MultipleSelect from "../../components/MultipleSelect/MultipleSelect";
import StandartButton from "../../components/StandartButton/StandartButton";
import { ISO639Lang } from "../../constans/languagesList";
import {
  Quiz,
  QuizEvaluationType,
  QuizQuestionType,
  QuizType,
} from "../../types/Quiz";
import Notify, { SuccesNotify } from "../../utils/toaster";
import InterpretationDimensionsEditor from "./InterpretationDimensionsEditor";
import s from "./SystemQuizDesigner.module.css";
import {
  buildQuizPayload,
  buildQuizUpdatePayload,
  canDeleteFormChapter,
  createEmptyAnswer,
  createEmptyChapter,
  createEmptyQuestion,
  createInitialQuizFormState,
  deleteFormChapter,
  evaluationTypeChangeLosesData,
  getChapterQuestions,
  getEvaluationTypeOptionsForQuizType,
  getFormQuestions,
  getQuestionChapterId,
  getQuestionTypeOptions,
  getQuizDisplayTitle,
  getRetakeSummary,
  getScoreQuestionMaximum,
  getScoreQuizMaximum,
  getTextForLanguage,
  hasLanguageContent,
  isFeedbackQuizType,
  languageOptions,
  moveFormChapter,
  moveFormQuestion,
  moveQuestionToChapter,
  normalizeEvaluationType,
  normalizeQuestionType,
  questionTypeChangeLosesData,
  QuizFormAnswer,
  QuizFormQuestion,
  QuizFormState,
  quizToFormState,
  quizTypeOptions,
  setTextForLanguage,
  shouldUseQuizScoreSettings,
  syncLocalizedTextLanguages,
  syncStateLanguages,
  validateQuizForm,
} from "./systemQuizModel";

type DesignerRoute =
  | { mode: "create"; quizId?: undefined }
  | { mode: "edit"; quizId: string };

type QuizLoadResponse = {
  status: boolean;
  message?: string;
  error?: string;
  quiz?: Quiz | null;
};

type PendingQuestionType = {
  questionId: string;
  nextType: QuizQuestionType;
} | null;

type ProjectQuizReturnContext = {
  companyId: string;
  projectId: string;
  bucketIndex?: string;
  projectTitle: string;
  bucketTitle: string;
};

const parseDesignerRoute = (pathname: string): DesignerRoute | null => {
  if (pathname === "/system-quizzes/new") return { mode: "create" };

  const editMatch = pathname.match(/^\/system-quizzes\/([^/]+)\/edit$/);
  if (editMatch) {
    return { mode: "edit", quizId: decodeURIComponent(editMatch[1]) };
  }

  return null;
};

const getQuizFromResponse = (response: QuizLoadResponse): Quiz | null =>
  response.status ? (response.quiz ?? null) : null;

const getQuizResponseMessage = (response: QuizLoadResponse) =>
  response.message || response.error || "";

const makeCopyId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const getProjectQuizReturnContext = (
  search: string,
): ProjectQuizReturnContext | null => {
  const searchParams = new URLSearchParams(search);
  if (searchParams.get("context") !== "project-quiz") return null;

  const projectId = searchParams.get("projectId");
  const companyId = searchParams.get("companyId");
  if (!projectId || !companyId) return null;

  return {
    companyId,
    projectId,
    bucketIndex: searchParams.get("bucketIndex") ?? undefined,
    projectTitle: searchParams.get("projectTitle") || "Project",
    bucketTitle: searchParams.get("bucketTitle") || "Project Quiz Bucket",
  };
};

const getProjectQuizReturnPath = (context: ProjectQuizReturnContext | null) => {
  if (!context) return "/system-quizzes";

  const searchParams = new URLSearchParams({ step: "Quiz" });
  if (context.bucketIndex) searchParams.set("bucketIndex", context.bucketIndex);

  return `/companies/${encodeURIComponent(context.companyId)}/projects/${encodeURIComponent(
    context.projectId,
  )}/edit?${searchParams.toString()}`;
};

const getQuestionPreview = (
  question: QuizFormQuestion,
  selectedLanguage: string,
) => {
  const preview =
    getTextForLanguage(question.text, selectedLanguage) ||
    question.text.find((item) => item.text.trim().length > 0)?.text ||
    "Untitled question";

  return preview.length > 140 ? `${preview.slice(0, 140)}...` : preview;
};

const duplicateQuestion = (question: QuizFormQuestion): QuizFormQuestion => ({
  ...question,
  id: makeCopyId("question"),
  originalId: undefined,
  text: question.text.map((item) => ({ ...item })),
  range: { ...question.range },
  correctRange: { ...question.correctRange },
  answers: question.answers.map((answer) => ({
    ...answer,
    id: makeCopyId("answer"),
    originalId: undefined,
    text: answer.text.map((item) => ({ ...item })),
  })),
});

type ResponsiveInputProps = ComponentProps<typeof Input> & {
  onCommit?: (value: string) => void;
};

const ResponsiveInput = ({
  inputValue,
  onChangeInput,
  onCommit,
  ...props
}: ResponsiveInputProps) => {
  const [draftValue, setDraftValue] = useState(inputValue);
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) setDraftValue(inputValue);
  }, [inputValue]);

  return (
    <Input
      {...props}
      inputValue={draftValue}
      onFocus={() => {
        focusedRef.current = true;
      }}
      onBlur={(event) => {
        focusedRef.current = false;
        onChangeInput(event.currentTarget.value);
        onCommit?.(event.currentTarget.value);
      }}
      onChangeInput={(value) => {
        setDraftValue(value);
        startTransition(() => onChangeInput(value));
      }}
    />
  );
};

type QuestionRenderBoundaryProps = {
  render: () => ReactNode;
  version: readonly unknown[];
};

const QuestionRenderBoundary = memo(
  ({ render }: QuestionRenderBoundaryProps) => <>{render()}</>,
  (previous, next) =>
    previous.version.length === next.version.length &&
    previous.version.every((value, index) =>
      Object.is(value, next.version[index]),
    ),
);

type ResponsiveTextareaProps = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  "value" | "onChange"
> & {
  value: string;
  onChangeValue: (value: string) => void;
};

const ResponsiveTextarea = ({
  value,
  onChangeValue,
  onFocus,
  onBlur,
  ...props
}: ResponsiveTextareaProps) => {
  const [draftValue, setDraftValue] = useState(value);
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) setDraftValue(value);
  }, [value]);

  return (
    <textarea
      {...props}
      value={draftValue}
      onFocus={(event) => {
        focusedRef.current = true;
        onFocus?.(event);
      }}
      onBlur={(event) => {
        focusedRef.current = false;
        onChangeValue(event.currentTarget.value);
        onBlur?.(event);
      }}
      onChange={(event) => {
        const nextValue = event.target.value;
        setDraftValue(nextValue);
        startTransition(() => onChangeValue(nextValue));
      }}
    />
  );
};

const SystemQuizDesigner = () => {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const location = useLocation();
  const route = useMemo(
    () => parseDesignerRoute(location.pathname),
    [location.pathname],
  );
  const projectQuizReturnContext = useMemo(
    () => getProjectQuizReturnContext(location.search),
    [location.search],
  );
  const [formState, setFormState] = useState<QuizFormState>(
    createInitialQuizFormState,
  );
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [loadedQuiz, setLoadedQuiz] = useState<Quiz | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const contextRef = useRef(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [pendingLanguages, setPendingLanguages] = useState<string[] | null>(
    null,
  );
  const [pendingQuestionType, setPendingQuestionType] =
    useState<PendingQuestionType>(null);
  const [pendingEvaluationType, setPendingEvaluationType] =
    useState<QuizEvaluationType | null>(null);
  const [collapsedQuestionIds, setCollapsedQuestionIds] = useState<string[]>(
    [],
  );
  const [formulaReviewRequired, setFormulaReviewRequired] = useState(false);
  const [chapterLabelsVersion, setChapterLabelsVersion] = useState(0);

  const isEditMode = route?.mode === "edit";
  const showScoreSettings = shouldUseQuizScoreSettings(
    formState.evaluationType,
  );
  const isFeedbackTemplate = isFeedbackQuizType(formState.type);
  const isResponseOnlyEvaluation =
    formState.evaluationType === QuizEvaluationType.RESPONSE_ONLY;
  const isInterpretationEvaluation =
    formState.evaluationType === QuizEvaluationType.INTERPRETATION;
  const chapterIdsKey = formState.chapters
    .map((chapter) => chapter.id)
    .join("|");
  const maximumScore = useMemo(
    () => getScoreQuizMaximum(formState),
    [formState.questions],
  );
  const orderedQuestions = useMemo(
    () => getFormQuestions(formState),
    [formState.questions, chapterIdsKey],
  );
  const questionIndexById = useMemo(
    () =>
      new Map(
        orderedQuestions.map((question, index) => [question.id, index] as const),
      ),
    [orderedQuestions],
  );
  const questionStructureKey = orderedQuestions
    .map(
      (question) =>
        `${question.id}:${getQuestionChapterId(formState, question) ?? ""}`,
    )
    .join("|");
  const hasQuestions = formState.questions.length > 0;
  const returnPath = getProjectQuizReturnPath(projectQuizReturnContext);
  const isProjectQuizCloneEdit = Boolean(projectQuizReturnContext);

  useEffect(() => {
    contextRef.current += 1;
    setSaving(false);
    let ignore = false;

    const loadQuiz = async () => {
      if (!route) {
        setErrorMessage("This System Quiz designer route is not supported.");
        return;
      }

      if (route.mode === "create") {
        setLoading(false);
        setErrorMessage("");
        setFormState(createInitialQuizFormState());
        setLoadedQuiz(null);
        setSelectedLanguage("");
        setCollapsedQuestionIds([]);
        setFormulaReviewRequired(false);
        return;
      }

      if (!token) {
        setErrorMessage(
          "Authentication token is missing. Please sign in again.",
        );
        return;
      }

      setLoading(true);
      setLoadedQuiz(null);
      setErrorMessage("");
      try {
        const response = (await QuizApi.getQuizById(
          token,
          route.quizId,
        )) as QuizLoadResponse;

        if (ignore) return;

        const quiz = getQuizFromResponse(response);

        if (!quiz) {
          setErrorMessage(
            getQuizResponseMessage(response) ||
              "Unable to load this System Quiz.",
          );
          return;
        }

        const nextState = quizToFormState(quiz);

        setLoadedQuiz(quiz);
        setFormState(nextState);
        setSelectedLanguage(nextState.selectedLanguages[0] ?? "");
        setCollapsedQuestionIds([]);
        setFormulaReviewRequired(false);
      } catch (error) {
        if (ignore) return;
        console.error("Error loading System Quiz:", error);
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "Something went wrong while loading this System Quiz.",
        );
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    loadQuiz();

    return () => {
      ignore = true;
      contextRef.current += 1;
    };
  }, [route, token]);

  const applyLanguages = (languages: string[]) => {
    setFormState((prev) => syncStateLanguages(prev, languages));
    setSelectedLanguage((prev) =>
      languages.includes(prev) ? prev : (languages[0] ?? ""),
    );
  };

  const requestLanguageChange = (languages: string[]) => {
    const removedLanguages = formState.selectedLanguages.filter(
      (language) => !languages.includes(language),
    );
    const hasRemovedContent = removedLanguages.some((language) =>
      hasLanguageContent(formState, language),
    );

    if (hasRemovedContent) {
      setPendingLanguages(languages);
      return;
    }

    applyLanguages(languages);
  };

  const updateFormText = (
    field: "title" | "description",
    language: string,
    value: string,
  ) => {
    setFormState((prev) => ({
      ...prev,
      [field]: setTextForLanguage(prev[field], language, value),
    }));
  };

  const updateQuestion = (
    questionId: string,
    updater: (question: QuizFormQuestion) => QuizFormQuestion,
  ) => {
    setFormState((prev) => ({
      ...prev,
      questions: prev.questions.map((question) =>
        question.id === questionId ? updater(question) : question,
      ),
    }));
  };

  const updateChapterText = (
    chapterId: string,
    field: "title" | "description",
    value: string,
  ) => {
    setFormState((prev) => ({
      ...prev,
      chapters: prev.chapters.map((chapter) =>
        chapter.id === chapterId
          ? {
              ...chapter,
              [field]: setTextForLanguage(
                syncLocalizedTextLanguages(
                  chapter[field],
                  prev.selectedLanguages,
                ),
                selectedLanguage,
                value,
              ),
            }
          : chapter,
      ),
    }));
  };

  const changeStructure = (change: (state: QuizFormState) => QuizFormState) => {
    setFormState(change);
    if (isInterpretationEvaluation) setFormulaReviewRequired(true);
  };

  const updateAnswer = (
    questionId: string,
    answerId: string,
    updater: (answer: QuizFormAnswer) => QuizFormAnswer,
  ) => {
    updateQuestion(questionId, (question) => ({
      ...question,
      answers: question.answers.map((answer) =>
        answer.id === answerId ? updater(answer) : answer,
      ),
    }));
  };

  const applyQuestionType = (
    questionId: string,
    nextType: QuizQuestionType,
  ) => {
    updateQuestion(questionId, (question) =>
      normalizeQuestionType(
        question,
        nextType,
        formState.selectedLanguages,
        formState.evaluationType as QuizEvaluationType,
      ),
    );
    if (isInterpretationEvaluation) setFormulaReviewRequired(true);
  };

  const requestEvaluationTypeChange = (nextType: QuizEvaluationType) => {
    if (evaluationTypeChangeLosesData(formState, nextType)) {
      setPendingEvaluationType(nextType);
      return;
    }

    setFormState((prev) => normalizeEvaluationType(prev, nextType));
    setFormulaReviewRequired(false);
  };

  const requestQuestionTypeChange = (
    question: QuizFormQuestion,
    nextType: QuizQuestionType,
  ) => {
    if (questionTypeChangeLosesData(question, nextType)) {
      setPendingQuestionType({ questionId: question.id, nextType });
      return;
    }

    applyQuestionType(question.id, nextType);
  };

  const toggleQuestion = (questionId: string) => {
    setCollapsedQuestionIds((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId],
    );
  };

  const addQuestion = (chapterId: string) => {
    changeStructure((prev) => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          ...createEmptyQuestion(prev.selectedLanguages, prev.evaluationType),
          chapterId,
        },
      ],
    }));
  };

  const removeQuestion = (questionId: string) => {
    setFormState((prev) => ({
      ...prev,
      questions: prev.questions.filter((item) => item.id !== questionId),
    }));
    setCollapsedQuestionIds((prev) => prev.filter((id) => id !== questionId));
    if (formState.evaluationType === QuizEvaluationType.INTERPRETATION) {
      setFormulaReviewRequired(true);
    }
  };

  const moveQuestion = (questionIndex: number, direction: -1 | 1) => {
    const question = orderedQuestions[questionIndex];
    if (!question) return;
    changeStructure((prev) => moveFormQuestion(prev, question.id, direction));
  };

  const duplicateQuestionAt = (question: QuizFormQuestion) => {
    setFormState((prev) => {
      const insertIndex = prev.questions.findIndex(
        (item) => item.id === question.id,
      );
      if (insertIndex === -1) return prev;

      return {
        ...prev,
        questions: [
          ...prev.questions.slice(0, insertIndex + 1),
          duplicateQuestion(question),
          ...prev.questions.slice(insertIndex + 1),
        ],
      };
    });

    if (formState.evaluationType === QuizEvaluationType.INTERPRETATION) {
      setFormulaReviewRequired(true);
    }
  };

  const saveQuiz = async () => {
    if (!token || !route || savingRef.current) return;

    const errors = validateQuizForm(formState);
    if (errors.length) {
      setErrorMessage(errors.slice(0, 8).join(" "));
      return;
    }

    savingRef.current = true;
    const context = contextRef.current;
    setSaving(true);
    setErrorMessage("");
    try {
      if (route.mode === "create") {
        const payload = buildQuizPayload(formState);
        const response = await QuizApi.createQuiz(token, { newQuiz: payload });
        if (context !== contextRef.current) return;
        if (response.status) {
          SuccesNotify("System Quiz created");
          navigate("/system-quizzes");
          return;
        }
        setErrorMessage(
          response.message || response.error || "Failed to create System Quiz.",
        );
        return;
      }

      const response = await QuizApi.updateQuiz(token, {
        updateData: buildQuizUpdatePayload(formState, route.quizId),
      });
      if (context !== contextRef.current) return;
      if (response.status) {
        SuccesNotify(
          isProjectQuizCloneEdit
            ? "Project Quiz clone saved"
            : "System Quiz saved",
        );
        navigate(
          !isProjectQuizCloneEdit && isFeedbackTemplate
            ? `/system-quizzes?selectedQuiz=${encodeURIComponent(route.quizId)}`
            : returnPath,
        );
        return;
      }
      setErrorMessage(
        response.message || response.error || "Failed to save System Quiz.",
      );
    } catch (error) {
      if (context !== contextRef.current) return;
      console.error("Error saving System Quiz:", error);
      Notify("Failed to save System Quiz");
      setErrorMessage("Something went wrong while saving this System Quiz.");
    } finally {
      savingRef.current = false;
      if (context === contextRef.current) setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={s.container}>
        <div className={s.state}>
          <Loader size={64} />
          <p className={s.stateText}>Loading System Quiz...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={s.container}>
      <header className={s.header}>
        <div>
          <h1 className={s.title}>
            {isProjectQuizCloneEdit
              ? "Edit Project Quiz"
              : isEditMode
                ? "Edit System Quiz"
                : "Create System Quiz"}
          </h1>
          <p className={s.subtitle}>
            {loadedQuiz
              ? getQuizDisplayTitle(loadedQuiz)
              : "Build a reusable multilingual quiz definition."}
          </p>
        </div>

        <div className={s.headerActions}>
          <button
            type="button"
            className={s.secondaryButton}
            onClick={() => navigate(returnPath)}
          >
            {isProjectQuizCloneEdit
              ? "Cancel and return to Project"
              : "Back to System Quizzes"}
          </button>
          <StandartButton
            buttonTitle={
              saving
                ? "Saving..."
                : isEditMode
                  ? "Save Changes"
                  : "Create System Quiz"
            }
            action={saveQuiz}
            disabled={saving || (isEditMode && !loadedQuiz)}
          />
        </div>
        {errorMessage && (
          <div className={s.alert} role="alert">
            {errorMessage}
          </div>
        )}
      </header>

      {projectQuizReturnContext && (
        <section className={s.projectQuizCloneBanner}>
          <div>
            <span className={s.projectQuizCloneEyebrow}>
              Project Quiz clone
            </span>
            <h2>
              {projectQuizReturnContext.projectTitle} ·{" "}
              {projectQuizReturnContext.bucketTitle}
            </h2>
            <p>
              You are editing this Project Quiz clone only. Changes do not
              affect the source System Quiz or sibling clones in other buckets.
            </p>
          </div>
        </section>
      )}

      <main className={s.content}>
        <fieldset
          className={s.editorFieldset}
          disabled={saving || (isEditMode && !loadedQuiz)}
        >
          <div className={s.form}>
            <section className={s.section}>
              <div className={s.sectionHeader}>
                <div>
                  <h2 className={s.sectionTitle}>General</h2>
                  <p className={s.sectionHint}>
                    Choose languages first, then fill text for the active
                    language.
                  </p>
                </div>
              </div>
              <div className={s.fieldGrid}>
                <div className={s.fieldBlock}>
                  <span className={s.fieldLabel}>Languages</span>
                  <MultipleSelect
                    data={languageOptions}
                    multiplie
                    checkBox
                    chip
                    value={formState.selectedLanguages}
                    setValue={(value) =>
                      requestLanguageChange(value as string[])
                    }
                    disableSortData
                  />
                </div>
                <div className={s.fieldBlock}>
                  <span className={s.fieldLabel}>System Quiz Type</span>
                  <MultipleSelect
                    data={quizTypeOptions}
                    multiplie={false}
                    value={formState.type}
                    setValue={(value) =>
                      setFormState((prev) => {
                        const nextType = value as QuizType;
                        const nextState = {
                          ...prev,
                          type: nextType,
                        };

                        if (
                          nextType === QuizType.FEEDBACK &&
                          prev.evaluationType === QuizEvaluationType.SCORE
                        ) {
                          return normalizeEvaluationType(
                            nextState,
                            QuizEvaluationType.RESPONSE_ONLY,
                          );
                        }

                        return nextState;
                      })
                    }
                    disableSortData
                  />
                </div>
                <div className={s.fieldBlock}>
                  <span className={s.fieldLabel}>Evaluation Type</span>
                  <MultipleSelect
                    data={getEvaluationTypeOptionsForQuizType(formState.type)}
                    multiplie={false}
                    value={formState.evaluationType}
                    setValue={(value) =>
                      requestEvaluationTypeChange(value as QuizEvaluationType)
                    }
                    disableSortData
                  />
                  <span className={s.helperText}>
                    Evaluation controls scoring behavior independently from the
                    product type.
                  </span>
                </div>
                {showScoreSettings && (
                  <div className={s.fieldBlock}>
                    <Input
                      lable="Minimum Points"
                      type="number"
                      inputValue={formState.minimumPoints}
                      minNumber={0}
                      onChangeInput={(value) =>
                        setFormState((prev) => ({
                          ...prev,
                          minimumPoints: value,
                        }))
                      }
                    />
                    <span className={s.helperText}>
                      Calculated maximum score: {maximumScore}
                    </span>
                  </div>
                )}
                {!isFeedbackTemplate && (
                  <div className={s.fieldBlock}>
                    <Input
                      lable="Allowed Retakes"
                      type="number"
                      inputValue={formState.retakeAmount}
                      minNumber={0}
                      onChangeInput={(value) =>
                        setFormState((prev) => ({
                          ...prev,
                          retakeAmount: value,
                        }))
                      }
                    />
                    <span className={s.helperText}>
                      {getRetakeSummary(formState.retakeAmount)}. Enter 0 to
                      allow unlimited retakes.
                    </span>
                  </div>
                )}
              </div>

              {!isFeedbackTemplate && (
                <div className={s.cloneDefaults}>
                  <div>
                    <h3 className={s.cloneDefaultsTitle}>
                      Project clone defaults
                    </h3>
                    <p className={s.sectionHint}>
                      {isProjectQuizCloneEdit
                        ? "These settings apply to this Project Quiz clone. Platform administrators can edit them regardless of Project-manager editability."
                        : "Future Project Quiz clones inherit these values. Existing clones are not changed later."}
                    </p>
                  </div>
                  <div className={s.cloneDefaultsControls}>
                    <div className={s.switchCard}>
                      <MuiSwitch
                        lable="Confidential answers"
                        checked={formState.isConfidential}
                        setChecked={(isConfidential) =>
                          setFormState((prev) => ({ ...prev, isConfidential }))
                        }
                      />
                      <span className={s.helperText}>
                        Project managers cannot view submitted raw answers.
                      </span>
                    </div>
                    <div className={s.switchCard}>
                      <MuiSwitch
                        lable="Editable by Project managers"
                        checked={formState.isEditable}
                        setChecked={(isEditable) =>
                          setFormState((prev) => ({ ...prev, isEditable }))
                        }
                      />
                      <span className={s.helperText}>
                        Platform administrators can always edit the quiz.
                      </span>
                    </div>
                    <div className={s.switchCard}>
                      <MuiSwitch
                        lable="Show score to participants"
                        checked={formState.isScoreShown}
                        setChecked={(isScoreShown) =>
                          setFormState((prev) => ({ ...prev, isScoreShown }))
                        }
                      />
                      <span className={s.helperText}>
                        Participants can see their score after submitting the
                        quiz.
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {formState.selectedLanguages.length > 0 && (
                <div className={s.languageTabs}>
                  {formState.selectedLanguages.map((language) => (
                    <button
                      key={language}
                      type="button"
                      className={`${s.languageButton} ${
                        selectedLanguage === language
                          ? s.activeLanguageButton
                          : ""
                      }`}
                      onClick={() => setSelectedLanguage(language)}
                    >
                      {ISO639Lang[language]}
                    </button>
                  ))}
                </div>
              )}

              {selectedLanguage && (
                <div className={s.fieldGrid}>
                  <ResponsiveInput
                    lable={`Title (${selectedLanguage})`}
                    inputValue={getTextForLanguage(
                      formState.title,
                      selectedLanguage,
                    )}
                    onChangeInput={(value) =>
                      updateFormText("title", selectedLanguage, value)
                    }
                  />
                  <div className={`${s.fieldBlock} ${s.fullWidthField}`}>
                    <span className={s.fieldLabel}>
                      Description ({selectedLanguage})
                    </span>
                    <ResponsiveTextarea
                      className={s.textarea}
                      value={getTextForLanguage(
                        formState.description,
                        selectedLanguage,
                      )}
                      onChangeValue={(value) =>
                        updateFormText(
                          "description",
                          selectedLanguage,
                          value,
                        )
                      }
                    />
                  </div>
                </div>
              )}
            </section>

            <section className={s.section}>
              <div className={s.sectionHeader}>
                <div>
                  <h2 className={s.sectionTitle}>Questions</h2>
                  <p className={s.sectionHint}>
                    {hasQuestions
                      ? `${formState.questions.length} question${
                          formState.questions.length === 1 ? "" : "s"
                        } in this quiz`
                      : "Add at least one question to make this quiz usable."}
                  </p>
                </div>
                <div className={s.sectionActions}>
                  {hasQuestions && (
                    <button
                      type="button"
                      className={s.secondaryButton}
                      onClick={() =>
                        setCollapsedQuestionIds((prev) =>
                          prev.length === formState.questions.length
                            ? []
                            : formState.questions.map(
                                (question) => question.id,
                              ),
                        )
                      }
                    >
                      {collapsedQuestionIds.length ===
                      formState.questions.length
                        ? "Expand All"
                        : "Collapse All"}
                    </button>
                  )}
                  <button
                    type="button"
                    className={s.primaryActionButton}
                    disabled={
                      !formState.selectedLanguages.length ||
                      !formState.evaluationType
                    }
                    onClick={() =>
                      setFormState((prev) => ({
                        ...prev,
                        chapters: [...prev.chapters, createEmptyChapter()],
                      }))
                    }
                    title={
                      formState.selectedLanguages.length &&
                      formState.evaluationType
                        ? "Add Quiz Chapter"
                        : "Select languages and an evaluation type first"
                    }
                  >
                    <AddIcon fontSize="small" />
                    Add Quiz Chapter
                  </button>
                </div>
              </div>

              <div className={s.questionList}>
                {formulaReviewRequired && (
                  <div className={s.formulaReviewWarning} role="alert">
                    <WarningAmberIcon aria-hidden="true" />
                    <div>
                      <strong>
                        Review interpretation formulas before saving
                      </strong>
                      <p>
                        Question references are positional. Confirm that Q1, Q2,
                        and other references still point to the intended
                        questions; formulas were not rewritten.
                      </p>
                    </div>
                  </div>
                )}
                {formState.chapters.map((chapter, chapterIndex) => (
                  <section
                    className={s.chapter}
                    key={chapter.id}
                    aria-labelledby={`chapter-${chapter.id}`}
                  >
                    <div className={s.sectionHeader}>
                      <div>
                        <h3
                          className={s.sectionTitle}
                          id={`chapter-${chapter.id}`}
                        >
                          {getTextForLanguage(
                            chapter.title,
                            selectedLanguage,
                          ).trim() || `Quiz Chapter ${chapterIndex + 1}`}
                        </h3>
                        {getTextForLanguage(
                          chapter.description,
                          selectedLanguage,
                        ).trim() && (
                          <p className={s.sectionHint}>
                            {getTextForLanguage(
                              chapter.description,
                              selectedLanguage,
                            )}
                          </p>
                        )}
                      </div>
                      <div className={s.sectionActions}>
                        <button
                          type="button"
                          className={s.iconButton}
                          aria-label={`Move Quiz Chapter ${chapterIndex + 1} up`}
                          disabled={chapterIndex === 0}
                          onClick={() =>
                            changeStructure((prev) =>
                              moveFormChapter(prev, chapter.id, -1),
                            )
                          }
                        >
                          <KeyboardArrowUpIcon fontSize="small" />
                        </button>
                        <button
                          type="button"
                          className={s.iconButton}
                          aria-label={`Move Quiz Chapter ${chapterIndex + 1} down`}
                          disabled={
                            chapterIndex === formState.chapters.length - 1
                          }
                          onClick={() =>
                            changeStructure((prev) =>
                              moveFormChapter(prev, chapter.id, 1),
                            )
                          }
                        >
                          <KeyboardArrowDownIcon fontSize="small" />
                        </button>
                        <button
                          type="button"
                          className={s.dangerButton}
                          aria-label={`Delete Quiz Chapter ${chapterIndex + 1}`}
                          disabled={
                            !canDeleteFormChapter(formState, chapter.id)
                          }
                          title="Only empty chapters can be deleted; keep at least one chapter."
                          onClick={() =>
                            changeStructure((prev) =>
                              deleteFormChapter(prev, chapter.id),
                            )
                          }
                        >
                          <DeleteIcon fontSize="small" />
                          Delete Chapter
                        </button>
                        <button
                          type="button"
                          className={s.primaryActionButton}
                          disabled={
                            !formState.selectedLanguages.length ||
                            !formState.evaluationType
                          }
                          onClick={() => addQuestion(chapter.id)}
                        >
                          <AddIcon fontSize="small" />
                          Add Question
                        </button>
                      </div>
                    </div>
                    {selectedLanguage && (
                      <div className={s.chapterInputs}>
                        <ResponsiveInput
                          lable={`Quiz Chapter ${chapterIndex + 1} Title (${selectedLanguage})`}
                          ariaLabel={`Quiz Chapter ${chapterIndex + 1} Title (${selectedLanguage})`}
                          required={false}
                          inputValue={getTextForLanguage(
                            chapter.title,
                            selectedLanguage,
                          )}
                          onChangeInput={(value) =>
                            updateChapterText(chapter.id, "title", value)
                          }
                          onCommit={() =>
                            setChapterLabelsVersion((version) => version + 1)
                          }
                        />
                        <div className={s.fieldBlock}>
                          <label
                            className={s.fieldLabel}
                            htmlFor={`chapter-description-${chapter.id}`}
                          >
                            Quiz Chapter {chapterIndex + 1} Description (
                            {selectedLanguage})
                          </label>
                          <ResponsiveTextarea
                            className={s.textarea}
                            id={`chapter-description-${chapter.id}`}
                            value={getTextForLanguage(
                              chapter.description,
                              selectedLanguage,
                            )}
                            onChangeValue={(value) =>
                              updateChapterText(
                                chapter.id,
                                "description",
                                value,
                              )
                            }
                          />
                        </div>
                      </div>
                    )}
                    {getChapterQuestions(formState, chapter.id).length ===
                      0 && (
                      <p className={s.sectionHint}>
                        This Quiz Chapter has no questions.
                      </p>
                    )}
                    {getChapterQuestions(formState, chapter.id).map(
                      (question) => {
                        const questionIndex =
                          questionIndexById.get(question.id) ?? -1;
                        const collapsed = collapsedQuestionIds.includes(
                          question.id,
                        );
                        return (
                          <QuestionRenderBoundary
                            key={question.id}
                            version={[
                              question,
                              questionIndex,
                              collapsed,
                              selectedLanguage,
                              showScoreSettings,
                              isResponseOnlyEvaluation,
                              isInterpretationEvaluation,
                              saving,
                              isEditMode && !loadedQuiz,
                              formState.type,
                              formState.evaluationType,
                              chapterIdsKey,
                              chapterLabelsVersion,
                              questionStructureKey,
                            ]}
                            render={() => (
                              <article className={s.questionCard}>
                            <button
                              type="button"
                              className={s.questionSummary}
                              onClick={() => toggleQuestion(question.id)}
                              aria-expanded={!collapsed}
                              aria-controls={`question-${question.id}`}
                            >
                              <span className={s.questionNumber}>
                                Q{questionIndex + 1}
                              </span>
                              <span className={s.questionSummaryText}>
                                <span className={s.questionTitle}>
                                  Question {questionIndex + 1}
                                </span>
                                <span className={s.questionPreview}>
                                  {getQuestionPreview(
                                    question,
                                    selectedLanguage,
                                  )}
                                </span>
                              </span>
                              <span className={s.questionMeta}>
                                <span>
                                  {question.type === QuizQuestionType.SINGLE
                                    ? "Single choice"
                                    : question.type ===
                                        QuizQuestionType.MULTIPLE
                                      ? "Multiple choice"
                                      : question.type === QuizQuestionType.TEXT
                                        ? "Text"
                                        : "Numeric range"}
                                </span>
                                {question.type !== QuizQuestionType.RANGE &&
                                  question.type !== QuizQuestionType.TEXT && (
                                    <span>
                                      {question.answers.length} answers
                                    </span>
                                  )}
                                {showScoreSettings && (
                                  <span>
                                    Maximum: {getScoreQuestionMaximum(question)}
                                  </span>
                                )}
                              </span>
                              <ExpandMoreIcon
                                className={`${s.expandIcon} ${
                                  collapsed ? "" : s.expandIconOpen
                                }`}
                              />
                            </button>

                            <AnimateHeight
                              id={`question-${question.id}`}
                              height={collapsed ? 0 : "auto"}
                              duration={220}
                            >
                              {!collapsed && (
                                <div className={s.questionBody}>
                                <div className={s.questionToolbar}>
                                  <div className={s.fieldBlock}>
                                    <MultipleSelect
                                      lable={`Move Q${questionIndex + 1} to Quiz Chapter`}
                                      ariaLabel={`Move Q${questionIndex + 1} to Quiz Chapter`}
                                      data={formState.chapters.map(
                                        (item, index) => ({
                                          value: item.id,
                                          lable:
                                            getTextForLanguage(
                                              item.title,
                                              selectedLanguage,
                                            ).trim() ||
                                            `Quiz Chapter ${index + 1}`,
                                        }),
                                      )}
                                      multiplie={false}
                                      disabled={
                                        saving || (isEditMode && !loadedQuiz)
                                      }
                                      value={
                                        getQuestionChapterId(
                                          formState,
                                          question,
                                        ) ?? ""
                                      }
                                      setValue={(value) =>
                                        changeStructure((prev) =>
                                          moveQuestionToChapter(
                                            prev,
                                            question.id,
                                            value as string,
                                          ),
                                        )
                                      }
                                      disableSortData
                                    />
                                  </div>
                                  <div
                                    className={s.orderControls}
                                    role="group"
                                    aria-label={`Reorder Q${questionIndex + 1}`}
                                  >
                                    <Tooltip title="Move question up">
                                      <span>
                                        <button
                                          type="button"
                                          className={s.iconButton}
                                          aria-label={`Move Q${questionIndex + 1} up`}
                                          disabled={
                                            questionIndex === 0 ||
                                            getQuestionChapterId(
                                              formState,
                                              orderedQuestions[
                                                questionIndex - 1
                                              ],
                                            ) !==
                                              getQuestionChapterId(
                                                formState,
                                                question,
                                              )
                                          }
                                          onClick={() =>
                                            moveQuestion(questionIndex, -1)
                                          }
                                        >
                                          <KeyboardArrowUpIcon fontSize="small" />
                                        </button>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Move question down">
                                      <span>
                                        <button
                                          type="button"
                                          className={s.iconButton}
                                          aria-label={`Move Q${questionIndex + 1} down`}
                                          disabled={
                                            questionIndex ===
                                              orderedQuestions.length - 1 ||
                                            getQuestionChapterId(
                                              formState,
                                              orderedQuestions[
                                                questionIndex + 1
                                              ],
                                            ) !==
                                              getQuestionChapterId(
                                                formState,
                                                question,
                                              )
                                          }
                                          onClick={() =>
                                            moveQuestion(questionIndex, 1)
                                          }
                                        >
                                          <KeyboardArrowDownIcon fontSize="small" />
                                        </button>
                                      </span>
                                    </Tooltip>
                                  </div>
                                  <button
                                    type="button"
                                    className={s.ghostButton}
                                    onClick={() =>
                                      duplicateQuestionAt(question)
                                    }
                                  >
                                    <ContentCopyIcon fontSize="small" />
                                    Duplicate
                                  </button>
                                  <button
                                    type="button"
                                    className={s.dangerButton}
                                    onClick={() => removeQuestion(question.id)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                    Delete
                                  </button>
                                </div>

                                <div className={s.fieldGrid}>
                                  <div className={s.fieldBlock}>
                                    <span className={s.fieldLabel}>
                                      Question Type
                                    </span>
                                    <MultipleSelect
                                      ariaLabel="Question Type"
                                      disabled={saving}
                                      data={getQuestionTypeOptions(
                                        formState.type,
                                        formState.evaluationType,
                                      )}
                                      multiplie={false}
                                      value={question.type}
                                      setValue={(value) =>
                                        requestQuestionTypeChange(
                                          question,
                                          value as QuizQuestionType,
                                        )
                                      }
                                      disableSortData
                                    />
                                  </div>
                                  {showScoreSettings &&
                                    (question.type !==
                                    QuizQuestionType.MULTIPLE ? (
                                      <Input
                                        lable={
                                          question.type ===
                                          QuizQuestionType.TEXT
                                            ? "Participation Points"
                                            : "Points"
                                        }
                                        ariaLabel={
                                          question.type ===
                                          QuizQuestionType.TEXT
                                            ? "Participation Points"
                                            : "Points"
                                        }
                                        ariaDescribedBy={
                                          question.type ===
                                          QuizQuestionType.TEXT
                                            ? `text-help-${question.id}`
                                            : undefined
                                        }
                                        type="number"
                                        minNumber={
                                          question.type ===
                                          QuizQuestionType.TEXT
                                            ? undefined
                                            : 1
                                        }
                                        step={
                                          question.type ===
                                          QuizQuestionType.TEXT
                                            ? "any"
                                            : undefined
                                        }
                                        inputValue={question.points}
                                        onChangeInput={(value) =>
                                          updateQuestion(
                                            question.id,
                                            (prevQuestion) => ({
                                              ...prevQuestion,
                                              points: value,
                                            }),
                                          )
                                        }
                                      />
                                    ) : (
                                      <div className={s.maximumCard}>
                                        <span className={s.maximumLabel}>
                                          Question maximum
                                        </span>
                                        <strong>
                                          {getScoreQuestionMaximum(question)} points
                                        </strong>
                                        <span className={s.helperText}>
                                          Sum of positive static points on
                                          correct answers.
                                        </span>
                                      </div>
                                    ))}
                                  {question.type === QuizQuestionType.TEXT && (
                                    <p
                                      className={`${s.helperText} ${s.fullWidthField}`}
                                      id={`text-help-${question.id}`}
                                    >
                                      {showScoreSettings
                                        ? "Any valid non-blank response earns all configured points as participation credit. Responses are not evaluated for correctness."
                                        : "Collect a qualitative response without points or correctness evaluation. Text questions cannot be referenced by interpretation formulas."}{" "}
                                      Respondents must enter 1–5,000 characters
                                      after trimming.
                                    </p>
                                  )}
                                  {question.type === QuizQuestionType.RANGE && (
                                    <div className={s.rangeEditor}>
                                      <div className={s.rangeEditorHeader}>
                                        <div>
                                          <span className={s.fieldLabel}>
                                            Allowed range
                                          </span>
                                          <span className={s.helperText}>
                                            Employees can submit any value
                                            inside these inclusive bounds.
                                          </span>
                                        </div>
                                        <strong>
                                          {question.range.from || "?"} to{" "}
                                          {question.range.to || "?"}
                                        </strong>
                                      </div>
                                      <div className={s.rangeInputs}>
                                        <Input
                                          lable="Allowed Range From"
                                          type="number"
                                          inputValue={question.range.from}
                                          onChangeInput={(value) =>
                                            updateQuestion(
                                              question.id,
                                              (prevQuestion) => ({
                                                ...prevQuestion,
                                                range: {
                                                  ...prevQuestion.range,
                                                  from: value,
                                                },
                                              }),
                                            )
                                          }
                                        />
                                        <Input
                                          lable="Allowed Range To"
                                          type="number"
                                          inputValue={question.range.to}
                                          onChangeInput={(value) =>
                                            updateQuestion(
                                              question.id,
                                              (prevQuestion) => ({
                                                ...prevQuestion,
                                                range: {
                                                  ...prevQuestion.range,
                                                  to: value,
                                                },
                                              }),
                                            )
                                          }
                                        />
                                      </div>
                                      {showScoreSettings && (
                                        <>
                                          <div className={s.rangeEditorHeader}>
                                            <div>
                                              <span className={s.fieldLabel}>
                                                Correct range
                                              </span>
                                              <span className={s.helperText}>
                                                Values inside this inclusive
                                                range award all question points.
                                              </span>
                                            </div>
                                            <strong>
                                              {question.correctRange.from ||
                                                "?"}{" "}
                                              to{" "}
                                              {question.correctRange.to || "?"}
                                            </strong>
                                          </div>
                                          <div className={s.rangeInputs}>
                                            <Input
                                              lable="Correct Range From"
                                              type="number"
                                              inputValue={
                                                question.correctRange.from
                                              }
                                              onChangeInput={(value) =>
                                                updateQuestion(
                                                  question.id,
                                                  (prevQuestion) => ({
                                                    ...prevQuestion,
                                                    correctRange: {
                                                      ...prevQuestion.correctRange,
                                                      from: value,
                                                    },
                                                  }),
                                                )
                                              }
                                            />
                                            <Input
                                              lable="Correct Range To"
                                              type="number"
                                              inputValue={
                                                question.correctRange.to
                                              }
                                              onChangeInput={(value) =>
                                                updateQuestion(
                                                  question.id,
                                                  (prevQuestion) => ({
                                                    ...prevQuestion,
                                                    correctRange: {
                                                      ...prevQuestion.correctRange,
                                                      to: value,
                                                    },
                                                  }),
                                                )
                                              }
                                            />
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  )}
                                  {selectedLanguage && (
                                    <div
                                      className={`${s.fieldBlock} ${s.fullWidthField}`}
                                    >
                                      <span className={s.fieldLabel}>
                                        Question Text ({selectedLanguage})
                                      </span>
                                      <ResponsiveTextarea
                                        className={s.textarea}
                                        aria-label={`Question Text (${selectedLanguage})`}
                                        value={getTextForLanguage(
                                          question.text,
                                          selectedLanguage,
                                        )}
                                        onChangeValue={(value) =>
                                          updateQuestion(
                                            question.id,
                                            (prevQuestion) => ({
                                              ...prevQuestion,
                                              text: setTextForLanguage(
                                                prevQuestion.text,
                                                selectedLanguage,
                                                value,
                                              ),
                                            }),
                                          )
                                        }
                                      />
                                    </div>
                                  )}
                                </div>

                                {question.type !== QuizQuestionType.RANGE &&
                                  question.type !== QuizQuestionType.TEXT && (
                                    <div className={s.choiceEditor}>
                                      <div className={s.answersHeader}>
                                        <span className={s.fieldLabel}>
                                          Answers
                                        </span>
                                        <Tooltip title="Add answer" arrow>
                                          <button
                                            type="button"
                                            className={s.iconButton}
                                            onClick={() =>
                                              updateQuestion(
                                                question.id,
                                                (prevQuestion) => ({
                                                  ...prevQuestion,
                                                  answers: [
                                                    ...prevQuestion.answers,
                                                    createEmptyAnswer(
                                                      formState.selectedLanguages,
                                                    ),
                                                  ],
                                                }),
                                              )
                                            }
                                          >
                                            <AddIcon fontSize="small" />
                                          </button>
                                        </Tooltip>
                                      </div>

                                      {question.type ===
                                        QuizQuestionType.SINGLE &&
                                      showScoreSettings ? (
                                        <RadioGroup
                                          className={s.answers}
                                          value={
                                            question.answers.find(
                                              (answer) => answer.isCorrect,
                                            )?.id ?? ""
                                          }
                                          onChange={(event) => {
                                            const selectedAnswerId =
                                              event.target.value;
                                            updateQuestion(
                                              question.id,
                                              (prevQuestion) => ({
                                                ...prevQuestion,
                                                answers:
                                                  prevQuestion.answers.map(
                                                    (answer) => ({
                                                      ...answer,
                                                      isCorrect:
                                                        answer.id ===
                                                        selectedAnswerId,
                                                    }),
                                                  ),
                                              }),
                                            );
                                          }}
                                        >
                                          {question.answers.map(
                                            (answer, answerIndex) => (
                                              <div
                                                className={s.answerCard}
                                                key={answer.id}
                                              >
                                                <FormControlLabel
                                                  value={answer.id}
                                                  control={
                                                    <Radio
                                                      color="success"
                                                      sx={{
                                                        color: "#fff",
                                                        "&.Mui-checked": {
                                                          color: "#3fb082",
                                                        },
                                                      }}
                                                    />
                                                  }
                                                  label=""
                                                  className={s.answerSelector}
                                                />
                                                <div className={s.answerFields}>
                                                  <span
                                                    className={s.answerLabel}
                                                  >
                                                    Answer {answerIndex + 1}
                                                    {selectedLanguage
                                                      ? ` (${selectedLanguage})`
                                                      : ""}
                                                  </span>
                                                  <ResponsiveInput
                                                    inputValue={
                                                      selectedLanguage
                                                        ? getTextForLanguage(
                                                            answer.text,
                                                            selectedLanguage,
                                                          )
                                                        : ""
                                                    }
                                                    disabled={!selectedLanguage}
                                                    onChangeInput={(value) =>
                                                      updateAnswer(
                                                        question.id,
                                                        answer.id,
                                                        (prevAnswer) => ({
                                                          ...prevAnswer,
                                                          text: setTextForLanguage(
                                                            prevAnswer.text,
                                                            selectedLanguage,
                                                            value,
                                                          ),
                                                        }),
                                                      )
                                                    }
                                                  />
                                                  <FormControlLabel
                                                    label="Risk flag"
                                                    control={
                                                      <Checkbox
                                                        checked={
                                                          answer.riskFlag ??
                                                          false
                                                        }
                                                        disabled={saving}
                                                        color="success"
                                                        inputProps={{
                                                          "aria-label": `Q${questionIndex + 1} Answer ${answerIndex + 1} Risk flag`,
                                                        }}
                                                        onChange={(event) =>
                                                          updateAnswer(
                                                            question.id,
                                                            answer.id,
                                                            (prevAnswer) => ({
                                                              ...prevAnswer,
                                                              riskFlag:
                                                                event.target
                                                                  .checked,
                                                            }),
                                                          )
                                                        }
                                                      />
                                                    }
                                                  />
                                                </div>
                                                <Tooltip
                                                  title="Remove answer"
                                                  arrow
                                                >
                                                  <button
                                                    type="button"
                                                    className={s.iconButton}
                                                    disabled={
                                                      question.answers.length <=
                                                      2
                                                    }
                                                    onClick={() =>
                                                      updateQuestion(
                                                        question.id,
                                                        (prevQuestion) => ({
                                                          ...prevQuestion,
                                                          answers:
                                                            prevQuestion.answers.filter(
                                                              (item) =>
                                                                item.id !==
                                                                answer.id,
                                                            ),
                                                        }),
                                                      )
                                                    }
                                                  >
                                                    <DeleteIcon fontSize="small" />
                                                  </button>
                                                </Tooltip>
                                              </div>
                                            ),
                                          )}
                                        </RadioGroup>
                                      ) : (
                                        <div className={s.answers}>
                                          {question.answers.map(
                                            (answer, answerIndex) => (
                                              <div
                                                className={`${s.answerCard} ${
                                                  !showScoreSettings
                                                    ? s.interpretationAnswerCard
                                                    : ""
                                                }`}
                                                key={answer.id}
                                              >
                                                {showScoreSettings && (
                                                  <Tooltip
                                                    title="Correct answer"
                                                    arrow
                                                  >
                                                    <Checkbox
                                                      checked={answer.isCorrect}
                                                      color="success"
                                                      inputProps={{
                                                        "aria-label": `Mark answer ${
                                                          answerIndex + 1
                                                        } correct`,
                                                      }}
                                                      onChange={(event) =>
                                                        updateAnswer(
                                                          question.id,
                                                          answer.id,
                                                          (prevAnswer) => ({
                                                            ...prevAnswer,
                                                            isCorrect:
                                                              event.target
                                                                .checked,
                                                          }),
                                                        )
                                                      }
                                                      sx={{
                                                        color: "#fff",
                                                        "&.MuiCheckbox-root": {
                                                          marginBottom: "5px",
                                                        },
                                                        "&.Mui-checked": {
                                                          color: "#3fb082",
                                                        },
                                                      }}
                                                    />
                                                  </Tooltip>
                                                )}
                                                <div className={s.answerFields}>
                                                  <span
                                                    className={s.answerLabel}
                                                  >
                                                    Answer {answerIndex + 1}
                                                    {selectedLanguage
                                                      ? ` (${selectedLanguage})`
                                                      : ""}
                                                  </span>
                                                  <ResponsiveInput
                                                    inputValue={
                                                      selectedLanguage
                                                        ? getTextForLanguage(
                                                            answer.text,
                                                            selectedLanguage,
                                                          )
                                                        : ""
                                                    }
                                                    disabled={!selectedLanguage}
                                                    onChangeInput={(value) =>
                                                      updateAnswer(
                                                        question.id,
                                                        answer.id,
                                                        (prevAnswer) => ({
                                                          ...prevAnswer,
                                                          text: setTextForLanguage(
                                                            prevAnswer.text,
                                                            selectedLanguage,
                                                            value,
                                                          ),
                                                        }),
                                                      )
                                                    }
                                                  />
                                                  <FormControlLabel
                                                    label="Risk flag"
                                                    control={
                                                      <Checkbox
                                                        checked={
                                                          answer.riskFlag ??
                                                          false
                                                        }
                                                        disabled={saving}
                                                        color="success"
                                                        inputProps={{
                                                          "aria-label": `Q${questionIndex + 1} Answer ${answerIndex + 1} Risk flag`,
                                                        }}
                                                        onChange={(event) =>
                                                          updateAnswer(
                                                            question.id,
                                                            answer.id,
                                                            (prevAnswer) => ({
                                                              ...prevAnswer,
                                                              riskFlag:
                                                                event.target
                                                                  .checked,
                                                            }),
                                                          )
                                                        }
                                                      />
                                                    }
                                                  />
                                                </div>
                                                {!isResponseOnlyEvaluation && (
                                                  <div
                                                    className={
                                                      s.staticPointsField
                                                    }
                                                  >
                                                    <span
                                                      className={s.label}
                                                    >{`Answer ${
                                                      answerIndex + 1
                                                    } Static Points`}</span>
                                                    <span
                                                      className={s.helperText}
                                                    >
                                                      {showScoreSettings
                                                        ? answer.isCorrect
                                                          ? "Correct answers require a positive value."
                                                          : "Incorrect answers require zero or a negative value."
                                                        : "Signed integer used by the interpretation formula."}
                                                    </span>
                                                    <Input
                                                      ariaLabel={`Answer ${
                                                        answerIndex + 1
                                                      } Static Points`}
                                                      type="number"
                                                      inputValue={
                                                        answer.staticPoints
                                                      }
                                                      onChangeInput={(value) =>
                                                        updateAnswer(
                                                          question.id,
                                                          answer.id,
                                                          (prevAnswer) => ({
                                                            ...prevAnswer,
                                                            staticPoints: value,
                                                          }),
                                                        )
                                                      }
                                                    />
                                                  </div>
                                                )}
                                                <Tooltip
                                                  title="Remove answer"
                                                  arrow
                                                >
                                                  <button
                                                    type="button"
                                                    className={s.iconButton}
                                                    disabled={
                                                      question.answers.length <=
                                                      2
                                                    }
                                                    onClick={() =>
                                                      updateQuestion(
                                                        question.id,
                                                        (prevQuestion) => ({
                                                          ...prevQuestion,
                                                          answers:
                                                            prevQuestion.answers.filter(
                                                              (item) =>
                                                                item.id !==
                                                                answer.id,
                                                            ),
                                                        }),
                                                      )
                                                    }
                                                  >
                                                    <DeleteIcon fontSize="small" />
                                                  </button>
                                                </Tooltip>
                                              </div>
                                            ),
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </AnimateHeight>
                              </article>
                            )}
                          />
                        );
                      },
                    )}
                  </section>
                ))}
              </div>
            </section>

            {isInterpretationEvaluation && (
              <InterpretationDimensionsEditor
                interpretations={formState.interpretations}
                questions={orderedQuestions}
                languages={formState.selectedLanguages}
                selectedLanguage={selectedLanguage}
                onChange={(interpretations) =>
                  setFormState((prev) => ({ ...prev, interpretations }))
                }
              />
            )}
          </div>
        </fieldset>
      </main>

      <ConfirmationModal
        confirmFunc={() => {
          if (!pendingLanguages) return;
          applyLanguages(pendingLanguages);
          setPendingLanguages(null);
        }}
        confirmTitle="Removing a language will delete its quiz text. Continue?"
        modalVisible={Boolean(pendingLanguages)}
        setModalVisible={() => setPendingLanguages(null)}
      />
      <ConfirmationModal
        confirmFunc={() => {
          if (!pendingQuestionType) return;
          applyQuestionType(
            pendingQuestionType.questionId,
            pendingQuestionType.nextType,
          );
          setPendingQuestionType(null);
        }}
        confirmTitle="Changing this question type will clear incompatible answer, range, or point settings. Continue?"
        modalVisible={Boolean(pendingQuestionType)}
        setModalVisible={() => setPendingQuestionType(null)}
      />
      <ConfirmationModal
        confirmFunc={() => {
          if (!pendingEvaluationType) return;
          setFormState((prev) =>
            normalizeEvaluationType(prev, pendingEvaluationType),
          );
          setFormulaReviewRequired(false);
          setPendingEvaluationType(null);
        }}
        confirmTitle="Changing evaluation type will clear incompatible grading or interpretation settings. Continue?"
        modalVisible={Boolean(pendingEvaluationType)}
        setModalVisible={() => setPendingEvaluationType(null)}
      />
    </div>
  );
};

export default SystemQuizDesigner;
